import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import {
  Bold, Italic, Underline as UnderlineIcon, Heading2, Heading3,
  List, ListOrdered, Quote, Link2, Undo2, Redo2, Minus,
} from 'lucide-react';

/**
 * RichTextEditor — éditeur WYSIWYG minimaliste basé sur TipTap.
 * Génère du HTML propre pour être stocké tel quel puis rendu côté public.
 *
 * Props:
 *  - value  : string HTML initial
 *  - onChange(html) : callback appelé à chaque modification
 */
export default function RichTextEditor({ value, onChange }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        bulletList: { keepMarks: true },
        orderedList: { keepMarks: true },
        link: false,
        underline: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // TipTap renvoie <p></p> vide quand l'éditeur est vide
      onChange(html === '<p></p>' ? '' : html);
    },
  });

  // Synchronise si `value` change de l'extérieur (ex: après génération IA)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || '') !== current) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL du lien', previousUrl || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const btn = (active, onClick, Icon, title, testId) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testId}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 32, height: 32, borderRadius: 6, cursor: 'pointer',
        background: active ? '#1565C0' : '#fff',
        color: active ? '#fff' : '#334155',
        border: '1px solid #cbd5e1',
      }}
    >
      <Icon size={15} />
    </button>
  );

  return (
    <div data-testid="rich-text-editor" style={{ border: '1px solid #cbd5e1', borderRadius: 8, background: '#fff' }}>
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 4, padding: 6,
        borderBottom: '1px solid #e2e8f0', background: '#f8fafc',
        borderTopLeftRadius: 8, borderTopRightRadius: 8,
      }}>
        {btn(editor.isActive('bold'), () => editor.chain().focus().toggleBold().run(), Bold, 'Gras (Ctrl+B)', 'rte-bold')}
        {btn(editor.isActive('italic'), () => editor.chain().focus().toggleItalic().run(), Italic, 'Italique (Ctrl+I)', 'rte-italic')}
        {btn(editor.isActive('underline'), () => editor.chain().focus().toggleUnderline().run(), UnderlineIcon, 'Souligné (Ctrl+U)', 'rte-underline')}
        <span style={{ width: 1, background: '#cbd5e1', margin: '2px 4px' }} />
        {btn(editor.isActive('heading', { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), Heading2, 'Sous-titre (H2)', 'rte-h2')}
        {btn(editor.isActive('heading', { level: 3 }), () => editor.chain().focus().toggleHeading({ level: 3 }).run(), Heading3, 'Sous-titre (H3)', 'rte-h3')}
        <span style={{ width: 1, background: '#cbd5e1', margin: '2px 4px' }} />
        {btn(editor.isActive('bulletList'), () => editor.chain().focus().toggleBulletList().run(), List, 'Liste à puces', 'rte-ul')}
        {btn(editor.isActive('orderedList'), () => editor.chain().focus().toggleOrderedList().run(), ListOrdered, 'Liste numérotée', 'rte-ol')}
        {btn(editor.isActive('blockquote'), () => editor.chain().focus().toggleBlockquote().run(), Quote, 'Citation', 'rte-quote')}
        {btn(false, () => editor.chain().focus().setHorizontalRule().run(), Minus, 'Séparateur', 'rte-hr')}
        {btn(editor.isActive('link'), setLink, Link2, 'Insérer un lien', 'rte-link')}
        <span style={{ width: 1, background: '#cbd5e1', margin: '2px 4px' }} />
        {btn(false, () => editor.chain().focus().undo().run(), Undo2, 'Annuler', 'rte-undo')}
        {btn(false, () => editor.chain().focus().redo().run(), Redo2, 'Rétablir', 'rte-redo')}
      </div>
      <EditorContent editor={editor} className="rte-content" data-testid="rte-content" />
    </div>
  );
}
